/**
 * AuthProvider — React context for Supabase auth state
 * Tracks user, session, loading, and user tier (from profiles table).
 * The dashboard is publicly accessible; auth is only needed for gated features.
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext(null)

// Tier hierarchy for comparison
const TIER_LEVELS = { free: 1, pro: 2, premium: 3 }

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [userTier, setUserTier] = useState('free')

  // Fetch user tier from profiles table (graceful fallback if table doesn't exist)
  const fetchUserTier = useCallback(async (userId) => {
    if (!supabase || !userId) {
      setUserTier('free')
      return
    }
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('tier')
        .eq('id', userId)
        .single()

      if (error || !data) {
        // profiles table may not exist yet — default to free
        setUserTier('free')
      } else {
        setUserTier(data.tier || 'free')
      }
    } catch {
      setUserTier('free')
    }
  }, [])

  useEffect(() => {
    if (!supabase) {
      setLoading(false)
      return
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s)
      setUser(s?.user ?? null)
      if (s?.user) fetchUserTier(s.user.id)
      setLoading(false)
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, s) => {
        setSession(s)
        setUser(s?.user ?? null)
        if (s?.user) {
          fetchUserTier(s.user.id)
        } else {
          setUserTier('free')
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [fetchUserTier])

  const signIn = useCallback(async (email, password) => {
    if (!supabase) throw new Error('Supabase not configured')
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    return data
  }, [])

  const signUp = useCallback(async (email, password) => {
    if (!supabase) throw new Error('Supabase not configured')
    const { data, error } = await supabase.auth.signUp({ email, password })
    if (error) throw error
    return data
  }, [])

  const signInWithOAuth = useCallback(async (provider) => {
    if (!supabase) throw new Error('Supabase not configured')
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: window.location.origin,
        },
      })
      if (error) {
        // Supabase may return HTML error pages (e.g. 522) instead of JSON
        const msg = error.message || String(error)
        if (msg.includes('<') || msg.includes('>') || msg.includes('<!DOCTYPE') || msg.includes('<html')) {
          throw new Error('Authentication service temporarily unavailable. Please try again later.')
        }
        throw error
      }
      return data
    } catch (err) {
      // Catch network-level or non-JSON parse failures
      const msg = err.message || String(err)
      if (msg.includes('<') || msg.includes('>') || msg.includes('<!DOCTYPE') || msg.includes('<html')) {
        throw new Error('Authentication service temporarily unavailable. Please try again later.')
      }
      throw err
    }
  }, [])

  const signOut = useCallback(async () => {
    if (!supabase) return
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }, [])

  const isAuthenticated = !!user

  const value = {
    user,
    session,
    loading,
    userTier,
    signIn,
    signUp,
    signInWithOAuth,
    signOut,
    isAuthenticated,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

/**
 * Check if a user's tier meets the required level.
 * 'free' just means logged in. 'pro' and 'premium' require that tier or higher.
 */
export function hasTierAccess(userTier, requiredTier) {
  const userLevel = TIER_LEVELS[userTier] || 0
  const requiredLevel = TIER_LEVELS[requiredTier] || 0
  return userLevel >= requiredLevel
}
